import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { environment } from 'src/environments/environment';

// Fallback typing for google namespace
declare const google: any;

@Directive({
  selector: '[appGooglePlaces]'
})
export class GooglePlacesDirective implements OnInit, OnDestroy {
  @Input('appGooglePlaces') control?: AbstractControl | null;
  @Output() appGooglePlacesPlaceChanged = new EventEmitter<any>();

  private autocomplete: any;
  private static loaderPromise?: Promise<void>;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {
    this.ensurePlacesLoaded()
      .then(() => this.initAutocomplete())
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Google Places failed to load', err);
      });
  }

  ngOnDestroy(): void {
    try {
      if (this.autocomplete && google?.maps?.event) {
        google.maps.event.clearInstanceListeners(this.autocomplete);
      }
    } catch (_) {
      // ignore
    }
  }

  private ensurePlacesLoaded(): Promise<void> {
    // If already loaded
    if ((window as any).google?.maps?.places) return Promise.resolve();

    // Require API key in environment
    const apiKey: string | undefined = (environment as any).googleMapsKey || (environment as any).googleMaps?.apiKey;
    if (!apiKey) {
      return Promise.reject('Missing environment.googleMapsKey for Google Maps Places API');
    }

    if (!GooglePlacesDirective.loaderPromise) {
      GooglePlacesDirective.loaderPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=quarterly`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      });
    }
    return GooglePlacesDirective.loaderPromise;
  }

  private initAutocomplete(): void {
    if (!this.el?.nativeElement) return;
    const options: any = {
      // componentRestrictions: { country: ['co', 'mx', 'us'] }, // optional
      fields: ['place_id', 'name', 'formatted_address', 'geometry', 'address_components'],
      strictBounds: false,
    };
    this.autocomplete = new google.maps.places.Autocomplete(this.el.nativeElement, options);

    // El propio widget de Places fija autocomplete="off" en el input al
    // inicializarse (sobrescribiendo lo que traiga en el HTML). Chrome
    // ignora "off" en campos que detecta como dirección y sigue mostrando
    // su propio dropdown de direcciones guardadas encima del de Places —
    // un valor que Chrome no reconozca sí lo desactiva.
    this.el.nativeElement.setAttribute('autocomplete', `off-google-places-${Math.random().toString(36).slice(2)}`);

    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      const value = place?.formatted_address || place?.name || this.el.nativeElement.value;
      this.appGooglePlacesPlaceChanged.emit(place);
      // Write text into control if provided, else directly into input
      if (this.control) {
        this.control.setValue(value);
      } else {
        this.el.nativeElement.value = value;
      }
    });
  }
}
